import isEmpty from "lodash/isEmpty";
import trim from "lodash/trim";
import isEqual from "lodash/isEqual";
import uniqBy from "lodash/uniqBy";

import DesignNavigationItem from "./designNavigationItem";
import {Exception} from "../common/exception";
import {
  filterFeaturesByValues,
  getAllFeatureFilters,
  getFilterGroupValue,
  getFilterGroupValues
} from "../../misc/utils";
import store from "../../store";
import featureIsMatchingDisplayLogic, {checkIfInCatProperty, isVisibleForNavigation} from "../../misc/displayLogic";
import {addDisplayLogicCartItem, addToCart} from "../../actions/actionFunctions";
import {capitalizeFirstLetters} from "../../misc/helpers";
import Config, {IS_LIXIL_UI, IS_QKB_UI} from "../../config";
import {isNewNavEnabled} from "../../misc/newNavFunctions";
import {LOOK_ROOM_TYPES} from "../look/core/constants";
import {findItemWithMostSameWords} from "../look/core/featureNameMatching";
import {getCurrentRoomType} from "../appLayout";
import {getOptionsFromNavItemsForFeature} from "../look/lookNavigation/lookFunctions";
import {getColorFilterValue} from "../../misc/filtersFunctions";
import {
  featureHasLayers,
  getFeatureGroupVisualizerType,
  isFeatureNotInVisualizerNow,
  loadRelatedFeatures,
  setRoomNotInVisualizerProperty
} from "./featuresFunctions";
import {getFeatureCropCoors} from "../../misc/metaDataFunctions";
import {CABINET_GROUP, COUNTERTOP_GROUP} from "../kvComponent/core/constants";
import UNavItem from "./uNavItem";

/**
 * Every feature of a featuresList once (LayoutFeaturesList keeps them once already, plain legacy objects are merged)
 * @param featuresList {LayoutFeaturesList|Object.<string, Array>}
 * @returns {Array}
 */
export const getUniqueListFeatures = (featuresList) => {
  if (!featuresList) {
    return []
  }
  if (typeof featuresList.getAll === "function") {
    return featuresList.getAll()
  }
  const added = new Set();
  const result = [];
  Object.keys(featuresList).forEach(layoutName => {
    (featuresList[layoutName] || []).forEach(feature => {
      if (!added.has(feature)) {
        added.add(feature);
        result.push(feature);
      }
    })
  });
  return result
};

export default class PartCommon { // Common Class for KV & BV common methods (Feature -> IntermediatePart -> Part -> PartCommon)

  loadCommon(relatedItemName, RelatedItemClass, dbFeature, loadFeature = true, shouldLoadInDifferentGroups = false) {
    if (relatedItemName) {
      if (!this[relatedItemName]) {
        this[relatedItemName] = new RelatedItemClass(true);
        this.relatedItems.push(this[relatedItemName]);
      }
      if (!this.categoryEnabled) {
        this.categoryEnabled = true
      }
      this[relatedItemName].getParent = () => this;
      if (loadFeature) {
        this[relatedItemName].load(dbFeature);
      }
      return !shouldLoadInDifferentGroups
    }
    return false
  }

  createRelatedItems() {
    if (this.relatedGroupNames) {
      this.relatedGroupNames.forEach(groupName => {
        this.load({groupName}, false)
      })
    }
  }

  navigationNoMetaSubItems(filters, searchText) {
    return this.returnUniqueNavFeatures(this.getFeaturesBySearchText(this.getFilteredFeaturesByFilters(filters, this.features), searchText))
  }

  /**
   * Returns an array of navigation items related to the given parent item
   * i.e. Floor - will return all Floor features
   * @returns {Array.<DesignNavigationItem>}
   */
  navigationSubItems(filters, searchText) {
    return this.getFeaturesBySearchText(this.getFilteredFeaturesByFilters(filters), searchText)
      .map(_ => new DesignNavigationItem(_.featureName, _)); // 1) filter them by filters 2) filter them by search
  }

  navigationSubItemsWithFilterWithInactive(propName, propValue, featuresToCheck = this.features) {
    const refactoredArray = featuresToCheck.map((feature) => {
      feature.matchesMetaFailed = !(feature.matchesMetaForCondition(propName, propValue));
      return feature
    });
    return refactoredArray.map(_ => new DesignNavigationItem(_.featureName, _));
  }

  /**
   * Called before setLayers, so that each Feature can set the zIndex for its layers
   * @param zIndexes {object}
   */
  setZIndex(zIndexes) {
    if(zIndexes) {
      this.getRelatedItems().forEach(_ => _.setZIndex(zIndexes));
    }
  }

  /**
   * Each feature will load its data from teh data object which comes from REST
   * @param dbFeature {object}
   * @return boolean // needed for break logic
   */
  load(dbFeature) {
    console.log('exception load', this, dbFeature);
    Exception.NotImplementedBaseClass();
  }

  /**
   * Will return the list of sub-parts if any, i.e. for Vanity it will be Mirror, Toilet, Sink, ...
   * !@param withHidden {boolean}
   * @returns {Array.<DesignNavigationItem>}
   */
  subParts(withHidden = true) {
    return this.getRelatedItems()
      .filter(item => this.filterRelatedItem(item, withHidden))
      .map(_ => new DesignNavigationItem(_.partName, _));
  }

  filterRelatedItem(relatedItem, withHidden) {
    return withHidden
      ? relatedItem.visible && relatedItem.featureGroupEnabled
      : relatedItem.visible
      && relatedItem.featureGroupEnabled
      && (relatedItem.featureGroupVisible || (!relatedItem.featureGroupVisible && document.kvConfig.isBackend))
  }

  /**
   * Returns the navigation item to be displayed in navigation bar
   */
  navigationItem() {
    Exception.NotImplementedBaseClass(this);
  }

  /**
   * In case of Vanity is Custom & Complete.
   * Usually called for Complex parts, such as Vanities
   */
  subTypes() {
    // Exception.NotImplementedBaseClass(this);
    return []
  }

  /**
   * Converts a given sub-type to string
   // * @param subType
   * @returns {null}
   */
  subTypeToString() {
    return null;
  }

  /**
   * Checked whether the given sub-type has items, i.e. Vanity.Complete has
   */
  isSubTypeWithItems() {
    Exception.NotImplementedBaseClass(this);
  }

  activeSubType() {
    Exception.NotImplementedBaseClass(this);
  }

  getFilteredFeaturesByFilters(filters, featuresArray = this.features) {
    return !isEmpty(filters) ? filterFeaturesByValues(featuresArray, filters) : (featuresArray || []);
  }

  getFeaturesBySearchText(arrayToFilter, searchText) {
    if (!searchText) {
      return arrayToFilter
    }
    if (IS_LIXIL_UI) {
      return this.getFeaturesBySearchTextLixil(arrayToFilter, searchText)
    }
    const filterGroups = store.getState().filterState.filterGroups;
    const _searchText = trim(searchText.toLowerCase());
    return arrayToFilter.filter(feature => {
      let res = (!!feature);
      if (res) {
        // NOTE: was Object.assign(feature.dbFeature.filterValueIDs, ...) - that permanently merged the fab filters
        // INTO dbFeature.filterValueIDs on every search
        const featureFilterValueIds = Object.assign({}, feature.dbFeature.filterValueIDs, feature.dbFeature.fabFilterValueIDs);
        let isPartName = feature.getFeatureName && feature.getFeatureName().toLowerCase().includes(_searchText);
        let isFilterName = false;

        if(!isPartName) {
          for(let filterGroupId in featureFilterValueIds) {
            let filterGroupIds = featureFilterValueIds[filterGroupId];
            for(let filterIndex in filterGroupIds) {
              let filterId = filterGroupIds[filterIndex];
              if(filterGroups[filterGroupId] && filterGroups[filterGroupId].values) {
                let filterGroup = filterGroups.find(fg => fg.id === filterGroupId);
                if (filterGroup && filterGroup.values) {
                  let filter = filterGroup.values.find( fgv => fgv.id === filterId);
                  if (filter) {
                    isFilterName = (isFilterName || filter.displayText.toLowerCase().includes(_searchText) || filter.value.toLowerCase().includes(_searchText));
                  }
                }
              }
            }
          }
        }

        res = isPartName || isFilterName;
      }
      return res;
    })
  }

  getFeaturesBySearchTextLixil(arrayToFilter, searchText) {
    const filterGroups = store.getState().filterState.filterGroups;
    const collectionFilterGroup = filterGroups.find(group => group.name === "Collection");
    const _searchText = searchText.trim().toLowerCase();

    return arrayToFilter.filter(feature => {
      const featureFilterValueIds = getAllFeatureFilters(feature);
      const sku = (feature.dbFeature.sku || "").toLowerCase();
      const customDisplayName = feature.getFeatureName().toLowerCase();

      let res = (!!feature);
      if (res) {
        let matchByName = (sku && sku.includes(_searchText)) || (customDisplayName && customDisplayName.includes(_searchText));
        let isFilterName = false;

        if (!matchByName && collectionFilterGroup && Array.isArray(featureFilterValueIds[collectionFilterGroup.id])) {
          matchByName = featureFilterValueIds[collectionFilterGroup.id].some(filterId => {
            return collectionFilterGroup.values.some(fValue => {
              return (fValue.id === filterId) && fValue.displayText && fValue.displayText.toLowerCase().includes(_searchText)
            })
          })
        }

        if (!matchByName) {
          for(let filterGroupId in featureFilterValueIds) {
            let filterGroupIds = featureFilterValueIds[filterGroupId];
            for(let filterIndex in filterGroupIds) {
              let filterId = filterGroupIds[filterIndex];
              if(filterGroups[filterGroupId] && filterGroups[filterGroupId].values) {
                let filterGroup = filterGroups.find(fg => fg.id === filterGroupId);
                if (filterGroup && filterGroup.values) {
                  let filter = filterGroup.values.find(fgv => fgv.id === filterId);
                  if (filter) {
                    isFilterName = (isFilterName || filter.displayText.toLowerCase().includes(_searchText) || filter.value.toLowerCase().includes(_searchText));
                  }
                }
              }
            }
          }
        }

        res = matchByName || isFilterName;
      }
      return res;
    })
  }

  /**
   * Returns the fabricator feature id
   * @returns {int}
   */
  id() {
    return this.dbFeature.id;
  }

  /**
   * Returns the fabricator feature id
   * @param featureId {number}
   * @param partsToo {boolean}
   * @returns {feature}
   */
  getFeatureById(featureId, partsToo = false) {
    const neededId = +featureId;
    if (!this.features && (!partsToo || !this.featuresParts)) {
      console.error("This featureGroup has no features array:", this)
      return null
    }
    // if (partsToo) {
    //   return this.features.find(f => f.dbFeature.id === neededId) || this.featuresParts.find(f => f.dbFeature.id === neededId)
    // }
    // return this.features.find(f => f.dbFeature.id === neededId)
    let allFeatures = [];
    if (this.partId === "fridge" && this.featuresList) {
      allFeatures = getUniqueListFeatures(this.featuresList); // every layout, each feature once
    } else {
      allFeatures = partsToo ? [...this.features, ...this.featuresParts] : this.features
    }
    for (let i = 0; i < allFeatures.length; i++) {
      if (allFeatures[i].dbFeature.id === neededId) {
        return allFeatures[i];
      }
    }
    return null
  }

  getFeatureByPartName(partName, inParts = false) {
    if (!partName) {
      return null
    }
    partName = partName.split(" ").join("").toLowerCase();  // for example, "Natural Stone Sofitel Gold" & "Natural Stone  Sofitel Gold" - different names
    const arrayToFind = inParts && this.featureParts ? this.featureParts.map(feature => { return {part: feature} }) : this.navigationSubItems();
    const matchingItem = arrayToFind.find(navFeature => {
      const feature = navFeature.part;
      if (!feature.partName || feature.notAvailableInVisualizer) {
        return false
      }
      if (!feature._partNameWithoutSpaces) {
        feature._partNameWithoutSpaces = feature.getMatchingFeatureName().split(" ").join("").toLowerCase();
      }
      return partName === feature._partNameWithoutSpaces
    });
    return matchingItem ? matchingItem.part : null
  }

  getDefaultFeature() {

  }

  /**
   * Returns the active feature id
   * @param placeholdersNeeded
   * @returns {int}
   */
  activeFeatureId(placeholdersNeeded = false) {
    return placeholdersNeeded && this.placeholderFeature && !this.placeholderChanged
      ? this.placeholderFeature.dbFeature.id
      : this.activeFeature.dbFeature.id
  }

  /**
   * @param feature {Part}
   * @returns {*}
   */
  featureDefinitionId(feature = null) {
    const dbFeature = feature ? feature.dbFeature : this.dbFeature;
    return dbFeature.featureDefinitionID;
  }

  handleClick() {}

  disabledClick() {}

  /**
   * Used for metaData conditions
   * @returns Array
   */
  getArrayOfConditions() {
    return []
  }

  /**
   * Used for complex parts, i.e. Vanity - Complex, Custom
   // * @param subType
   */
  handleSubTypeClick() {
    Exception.NotImplementedBaseClass();
  }

  /**
   * Handles the click on sub-part buttons, i.e. for Vanity on its buttons such as Toilet, Sink...
   // * @param subPart
   */
  handleSubPartClick() {
    // Exception.NotImplementedBaseClass();
  }

  /**
   * Returns first value Id from a specific group
   * @param groupId
   */
  filterValue(groupId) {
    return this.dbFeature.filterValueIDs[groupId][0];
  }

  /**
   * Do something specific after layer changes
   // * @param layoutType
   */
  changeLayout() {

  }

  /**
   * Do something specific after Type Changed (returns null by default for all Parts)
   */
  setDefaultPartAfterSubTypeClick() {
    return null
  }

  getRelatedItems() {
    return this.relatedItems
  }

  /**
   * Set default layer for each relatedItem of the group.
   * @param layout {Layout}
   */
  setDefault(layout) {
    this.relatedItems.forEach(relatedItem => relatedItem.setDefault(layout));
  }

  setFeatureGroupDefault(layout, setNotInVisFeature = this.hasNoVisualizerLayers) {
    const conditions = this.getArrayOfConditions ? this.getArrayOfConditions() : null;
    let filteredFeatures = conditions ? (this.features || []).filter(feature => {
      return setNotInVisFeature
        ? feature.matchesMetaForArrayOfConditions(conditions)
        : feature.dbFeature.s3Bindings && feature.matchesMetaForArrayOfConditions(conditions)
    }) : (this.features || []);

    const isBV6 = document.kvConfig.visualizerType === "bvLook"; // for new bathroom only for now! to test
    if (isBV6 && this.activeFeature && this.selectedFeaturesHistory && !this.selectedFeaturesHistory.includes(this.activeFeature.getMatchingFeatureName())) {
      this.selectedFeaturesHistory.push(this.activeFeature.getMatchingFeatureName())
    }

    if (isBV6 && !filteredFeatures.length
      && (getCurrentRoomType(layout) === LOOK_ROOM_TYPES.as) && !(this.partId.includes("_wall") && this.hasAdditionalParts)) {
      filteredFeatures = this.getArrayOfConditions
        ? (this.featuresParts || []).filter(f => f.dbFeature.s3Bindings && f.matchesMetaForArrayOfConditions(conditions))
        : (this.featuresParts || []);
    }

    const matchingFeatureExist = this.tryToSetTheSameFeature(filteredFeatures, null);
    if (!matchingFeatureExist) {
      this.activeFeature = filteredFeatures[0];
    }
    this.promoteActiveNotInVisualizerFeature(filteredFeatures);
    const features = this.features || [];
    if (IS_LIXIL_UI && !this.activeFeature && !this.activeNotInVisualizerFeature && Array.isArray(features)
      && features.length && features.every(feature => feature.notAvailableInVisualizer)) {
      this.activeNotInVisualizerFeature = features[0];
    }
    // this.placeholderFeature = this.features.find((feature) => {
    //   return feature.dbFeature.displayName && feature.dbFeature.displayName.includes("laceholder");
    // });
    if (this.hasAdditionalParts) {
      this.activeFeatureParts = this.setActiveFeatureParts();
    }
    if (this.layerPositions) {
      if (this.activeFeature) {
        this.setLayerPositions()
      } else {
        Object.keys(this.layerPositions).forEach(key => {
          this.layerPositions[key] = null;
        })
      }
    }
  }

  /**
   * UNav (isNewNavEnabled): a feature WITH layers can be clicked while it doesn't match the current conditions
   * (e.g. it exists only in Room Layout 2 and we are in Room Layout 1). featureClick() keeps it in
   * `activeNotInVisualizerFeature` (via isFeatureNotInVisualizerNow). When the scenario changes
   * (layout switch -> setFeatureGroupDefault) and that feature can render now, make it the activeFeature.
   * Runs once per group per default/layout change - no loops over every feature.
   * @param filteredFeatures {[featureInterface]} features matching the current conditions
   */
  promoteActiveNotInVisualizerFeature(filteredFeatures = []) {
    const pendingFeature = this.activeNotInVisualizerFeature;
    if (!pendingFeature || pendingFeature.notAvailableInVisualizer || !featureHasLayers(pendingFeature)
      || !filteredFeatures.length || !isNewNavEnabled(getFeatureGroupVisualizerType(this))) {
      return
    }
    const pendingName = pendingFeature.getSplittedFeatureName();
    const renderableFeature = filteredFeatures.includes(pendingFeature)
      ? pendingFeature
      : filteredFeatures.find(feature => featureHasLayers(feature) && feature.getSplittedFeatureName() === pendingName);
    if (renderableFeature) {
      this.activeFeature = renderableFeature;
      this.activeNotInVisualizerFeature = null;
    }
  }

  tryToSetTheSameFeature(filteredFeatures = [], partName = "") {
    const currentActiveFeature = this.activeFeature;
    if (!currentActiveFeature || currentActiveFeature.partName) { // an active feature without partName is kept as is
      this.activeFeature = this.findTheSameFeature(filteredFeatures, partName, currentActiveFeature);
    }
    return !!this.activeFeature
  }

  /**
   * Finds (without setting it) the feature from filteredFeatures that is the "same" as currentActiveFeature:
   * 1) the same matching name, 2) the same color filter value, 3) one of the previously selected features
   * @param filteredFeatures {[featureInterface]}
   * @param partName {string}
   * @param currentActiveFeature {featureInterface}
   * @returns {featureInterface|undefined}
   */
  findTheSameFeature(filteredFeatures = [], partName = "", currentActiveFeature = this.activeFeature) {
    let foundFeature;
    if (currentActiveFeature && currentActiveFeature.partName) {
      const splittedFromTheLayoutName = partName && this.removeLayoutFromPartName
        ? this.removeLayoutFromPartName(partName)
        : currentActiveFeature.getMatchingFeatureName();
      foundFeature = filteredFeatures.find(feature => {
        return feature.getMatchingFeatureName() === splittedFromTheLayoutName
      });
    }
    if (!foundFeature && this.selectedFeaturesHistory) {
      for (let featureName of this.selectedFeaturesHistory.slice(0, 5)) { // check first five, no need to go further
        let foundFinally = filteredFeatures.find(feature => {
          return feature.getMatchingFeatureName() === featureName
        });
        const isBV6 = document.kvConfig.visualizerType === "bvLook"; // for new bathroom only for now
        if (!foundFinally && isBV6 && (this.partId === "vanity_mirror" || this.partId === "shower_wall"
          || this.partId === "split_wall")) {
          // pick the feature whose matching name shares the most words with the previous one
          // e.g. "osage birch eggshell fm5830 1 bertch" -> "osage birch eggshell fm2230 1 bertch"
          // parts can strip words that shouldn't be compared (e.g. shower size for shower walls)
          const prevName = this.removeSizeFromMatchingName ? this.removeSizeFromMatchingName(featureName) : featureName;
          foundFinally = findItemWithMostSameWords(prevName, filteredFeatures, feature => {
            return feature.getNoSizeMatchingName ? feature.getNoSizeMatchingName() : feature.getMatchingFeatureName()
          });
        }
        if (foundFinally) {
          foundFeature = foundFinally;
          break;
        }
      }
    }
    if (!foundFeature) {
      const filterGroups = store.getState().filterState.filterGroups;
      const colorFilterValue = getColorFilterValue(currentActiveFeature, filterGroups);
      if (colorFilterValue) {
        foundFeature = filteredFeatures.find(feature => {
          const currentColorFilterValue = getColorFilterValue(feature, filterGroups);
          return currentColorFilterValue && currentColorFilterValue.id === colorFilterValue.id
        });
      }
    }
    return foundFeature
  }

  getFeatureName(forQtCategory = "") {
    if (!this._featureName) {
      const featureGroup = this.getParent();
      const parent = featureGroup.getParent ? featureGroup.getParent() : featureGroup;
      const originalName = this.metaData.ActualDisplayedName && !parent.layout.isNewLayout
        ? this.dbFeature.customDisplayName && (this.dbFeature.customDisplayName !== this.dbFeature.sharedFeatureName)
          ? this.dbFeature.customDisplayName
          : this.metaData.ActualDisplayedName[0]
        : this.dbFeature.customDisplayName || this.partName || "";
      this._featureName = (originalName || "").replace((this.dbFeature.customGroupName || featureGroup.dbName || featureGroup.partName) + " - ", "");
    }
    if (forQtCategory) {
      return checkIfInCatProperty(this, "inCatName", this._featureName, forQtCategory) || this._featureName || "";
    }
    return this._featureName
  }

  getLowerCasedCustomDisplayName() {
    if (!this._lowercaseCustomDisplayName) {
      this._lowercaseCustomDisplayName = (this.dbFeature.customDisplayName || this.partName || '')
        .trim()
        .toLowerCase()
        .replace('room - ', '')
    }
    return this._lowercaseCustomDisplayName;
  }

  getLowerCasedSharedFeatureName() {
    if (!this._lowercaseSharedFeatureName) {
      this._lowercaseSharedFeatureName = (this.dbFeature.sharedFeatureName || this.partName || '')
        .trim()
        .toLowerCase()
        .replace('room - ', '')
    }
    return this._lowercaseSharedFeatureName;
  }

  /**
   * @param dbFeaturesArray {[dbFeatureInterface]}
   * @param pageSize {number}
   * @param setNextPage {boolean}
   * @param layoutAbbr {string}
   * @param loadedPage {number|null}
   * @param fromSearch {boolean}
   * @param setCustomConfigurationsAfter {boolean}
   */
  setGroupFeatures(dbFeaturesArray,
                   pageSize = 1000,
                   setNextPage = true,
                   layoutAbbr = "",
                   loadedPage = null,
                   fromSearch = false,
                   setCustomConfigurationsAfter = true) {

    const layout = this.layout || this.getParent().layout;
    const layoutToSet = layoutAbbr || layout.getCurrentLayoutAbbreviation();

    if (setNextPage) {
      // this.nextFeaturesPageToLoad += 1;
      let nextFeaturesPageToLoad = 1;
      const addFromPageSize = pageSize === 6000 ? 3 : 1;
      if (this.nextFeaturesPageToLoadArray) {
        nextFeaturesPageToLoad = Number.isInteger(loadedPage)
          ? this.nextFeaturesPageToLoadArray[layoutToSet].page + addFromPageSize
          : this.nextFeaturesPageToLoadArray[layoutToSet].page + 1
      } else {
        nextFeaturesPageToLoad = Number.isInteger(loadedPage)
          ? this.nextFeaturesPageToLoad + addFromPageSize
          : this.nextFeaturesPageToLoad + 1;
      }
      // let nextFeaturesPageToLoad = Number.isInteger(loadedPage)
      //   ? this.nextFeaturesPageToLoad + (pageSize === 6000 ? 3 : 1)
      //   : this.nextFeaturesPageToLoadArray ? this.nextFeaturesPageToLoadArray[layoutToSet].page + 1 : this.nextFeaturesPageToLoad + 1;
      // if ((pageSize / 1000 - 1) === 1) {
      //   nextFeaturesPageToLoad += 1
      // }
      this.nextFeaturesPageToLoad = nextFeaturesPageToLoad;
      if (this.nextFeaturesPageToLoadArray) {
        this.nextFeaturesPageToLoadArray[layoutToSet].page = nextFeaturesPageToLoad;
      }
      this.allFeaturesReceived = true;
    }
    if (!fromSearch) {
      if (this.nextFeaturesPageToLoadArray) {
        this.nextFeaturesPageToLoadArray[layoutToSet].allReceived = dbFeaturesArray.length < pageSize;
        const {bvPresets} = store.getState().bathroomVisualizerState;
        if (bvPresets && bvPresets.length) {
          const uniqueLayoutsTypes = bvPresets.reduce((total, preset) => {
            if (!total.some(layoutType => layoutType === preset.layout_type)) {
              total.push(preset.layout_type)
            }
            return total
          }, []);
          this.realFeaturesReceived = uniqueLayoutsTypes.every(layoutNumber => {
            const layoutName = layout.getCurrentLayoutName(layoutNumber);
            return this.nextFeaturesPageToLoadArray[layoutName] && this.nextFeaturesPageToLoadArray[layoutName].allReceived
          });
        } else {
          this.realFeaturesReceived = dbFeaturesArray.length < pageSize;
        }
      } else {
        this.realFeaturesReceived = dbFeaturesArray.length < pageSize;
      }
    }

    layout.loadNewFeatures(dbFeaturesArray, this, setCustomConfigurationsAfter);

  }

  getRealFeaturesReceivedProperty() {
    const layout = this.layout || this.getParent().layout;
    const currentLayoutAbbr = layout.getCurrentLayoutAbbreviation();
    if (this.nextFeaturesPageToLoadArray && !this.nextFeaturesPageToLoadArray[currentLayoutAbbr]) {
      console.error("NextFeaturesPageToLoadArray is missing layout", );
    }
    return this.nextFeaturesPageToLoadArray && this.nextFeaturesPageToLoadArray[currentLayoutAbbr]
      ? this.nextFeaturesPageToLoadArray[currentLayoutAbbr].allReceived
      : this.realFeaturesReceived
  }

  getNextPageToLoad(currentLayoutAbbr) {
    // intermediatePart
    if (this.nextFeaturesPageToLoadArray) {
      if (!currentLayoutAbbr) {
        const layout = this.layout || this.getParent().layout;
        currentLayoutAbbr = layout.getCurrentLayoutAbbreviation();
      }
      return this.nextFeaturesPageToLoadArray[currentLayoutAbbr].page
    }
    return this.nextFeaturesPageToLoad
  }

  getCollectionName(filterGroups) {
    if (this._collectionName) {
      return this._collectionName
    }
    const collectionFg = filterGroups ? filterGroups.find(fGroup => fGroup.name === "Collection") : null;
    if (collectionFg) {
      const featureCollectionValue = getFilterGroupValues(this, collectionFg);
      if (Array.isArray(featureCollectionValue)) {
        const featureCollectionValueObject = getFilterGroupValue(featureCollectionValue[0], collectionFg);
        if (featureCollectionValueObject) {
          this._collectionName = capitalizeFirstLetters(featureCollectionValueObject.displayText);
          return this._collectionName
        }
      }
    }
    return ""
  }

  getFeatureSavedFilterName(filterGroups, filterName, property) {
    if (!this._filterNames) {
      this._filterNames = {};
    }
    if (this._filterNames[property]) {
      return this._filterNames[property]
    }
    const collectionFg = filterGroups ? filterGroups.find(fGroup => fGroup.name === filterName) : null;
    if (collectionFg) {
      const featureCollectionValue = getFilterGroupValues(this, collectionFg);
      if (Array.isArray(featureCollectionValue)) {
        const featureCollectionValueObject = getFilterGroupValue(featureCollectionValue[0], collectionFg);
        if (featureCollectionValueObject) {
          this._filterNames[property] = capitalizeFirstLetters(featureCollectionValueObject.displayText || featureCollectionValueObject.value);
          return this._filterNames[property]
        }
      }
    }
    return ""
  }

  getColorName(filterGroups) {
    if (this._colorName) {
      return this._colorName
    }
    const colorFilterGroup = filterGroups ? filterGroups.find(fGroup => fGroup.name === "Color") : null;

    if (colorFilterGroup) {
      const featureCollectionValue = getFilterGroupValues(this, colorFilterGroup);
      if (Array.isArray(featureCollectionValue)) {
        const featureCollectionValueObject = getFilterGroupValue(featureCollectionValue[0], colorFilterGroup);
        if (featureCollectionValueObject) {
          this._colorName = capitalizeFirstLetters(featureCollectionValueObject.displayText);
          return this._colorName
        }
      }
    }
    return ""
  }

  getCustomFullDisplayName() {
    return this.getSplittedFeatureName() // is different in bvLook visualizer
  }

  getMatchingOptionsName() {
    return this.getSplittedFeatureName() // is different in bvLook visualizer
  }

  /**
   * Update visibility for all layers.
   * Each feature decides on its own what layers are needed.
   * In case when feature must be invisible it must return null, not an empty array
   * @param layout {Layout}
   * @param placeholdersNeeded {boolean}
   */
  setLayers(layout, placeholdersNeeded) {
    let layers = [];
    this.getRelatedItems()
      .filter(relItem => relItem.featureGroupEnabled)
      .forEach(relatedItem => {
        relatedItem.setLayers(layout);
        let activeFeatures = [];
        if (relatedItem.layerPositions) {
          Object.keys(relatedItem.layerPositions).forEach(key => {
            if (relatedItem.layerPositions[key]) {
              activeFeatures.push(relatedItem.layerPositions[key])
            }
          })
        } else {
          if (placeholdersNeeded && !relatedItem.placeholderChanged && relatedItem.placeholderFeature) {
            activeFeatures.push(relatedItem.placeholderFeature)
          } else if (relatedItem.activeFeature && relatedItem.partId !== "attachment") {
            activeFeatures.push(relatedItem.activeFeature)
          }
        }
        if (relatedItem.activeFeatureParts && relatedItem.activeFeatureParts.length) {
          activeFeatures = [...activeFeatures, ...relatedItem.activeFeatureParts]
        }
        activeFeatures.forEach((activeFeaturePart) => {
          if (activeFeaturePart && activeFeaturePart.layer.src) {
            // cabinetActiveFeature.layer.partId = cabinetActiveFeature.partId;
            activeFeaturePart.layer.coords = activeFeaturePart.metaData["Coords"];
            activeFeaturePart.layer.partId = activeFeaturePart.partId;
            activeFeaturePart.layer.layerPosition = activeFeaturePart.metaData.layerPosition;
            activeFeaturePart.layer.cropCoords = getFeatureCropCoors(activeFeaturePart);
            layers.push(activeFeaturePart.layer);
          }
        });
      });
    return layers.length ? layers : null;
  }

  // setPrevioslySelected(featureGroup) { // featureGroup is current active featureGroup from the click
  setPrevioslySelected() { // parent scope
    this.relatedItems.forEach(relatedItem => {
      relatedItem.activeFeatureBeforeAllWalls = !relatedItem.placeholderChanged
        ? "placeholder"
        : relatedItem.activeFeature;
    });
  }

  /**
   * Appends the features of `layoutName` whose splitted name is not added yet
   * @param layoutName {string}
   * @param forSpecificScene {string|null}
   * @param allFeatures {[featureInterface]} result array (mutated)
   * @param addedNames {Set<string>} names already in allFeatures (pass the same Set between calls - O(1) instead of O(n) per feature)
   */
  getFeaturesFromLayout(layoutName, forSpecificScene, allFeatures, addedNames = null) {
    if (!addedNames) {
      addedNames = new Set(allFeatures.map(addedFeature => addedFeature.getSplittedFeatureName()));
    }
    const addFrom = (list) => {
      for (let i = 0; i < list.length; i++) {
        const feature = list[i];
        if (forSpecificScene && feature.metaData["RoomScene"] && !feature.metaData["RoomScene"].includes(forSpecificScene)) {
          continue
        }
        const featureName = feature.getSplittedFeatureName();
        if (!addedNames.has(featureName)) {
          addedNames.add(featureName);
          allFeatures.push(feature);
        }
      }
    };
    addFrom(this.featuresList[layoutName] || []);
    if (this.layerPositions) {
      addFrom(this.featuresPartsList[layoutName] || []);
    }
    return allFeatures
  }

  getFeaturesForNavigationFrom(restOptions = {}) {
    const {returnAllFeatures, forSpecificLayout, forSpecificScene} = restOptions;
    if (returnAllFeatures) {
      const layout = this.getParent().layout;
      let allFeatures = [];
      const addedNames = new Set(); // shared between the calls below: O(1) de-dupe by splitted name
      // 1) first add current layout features or specific layout features
      allFeatures = this.getFeaturesFromLayout(forSpecificLayout || ("Layout" + layout.type), forSpecificScene, allFeatures, addedNames)
      if (forSpecificLayout) {
        return allFeatures
      }
      // 2) if we need all features - check another layouts
      const {bvPresets} = store.getState().bathroomVisualizerState;
      // const checkLayouts = layoutNames.filter(layoutName => {
      //   if (forSpecificLayout) {
      //     return forSpecificLayout === layoutName
      //   }
      //   return bvPresets.some(bvPreset => bvPreset.layout_type === +(layoutName.replace("Layout", "")))
      // })

      // 2) next popular rooms are 11 for AS and 3 for grohe, check them
      if (layout.type !== 11 && (document.kvConfig.visualizerType === "bvLook")) {
        allFeatures = this.getFeaturesFromLayout("Layout11", "1", allFeatures, addedNames);
      }
      // if (layout.type !== 3) {
      //   allFeatures = this.getFeaturesFromLayout("Layout3", "1", allFeatures);
      // }
      // 3) select all features from scene 1
      bvPresets.forEach(bvPreset => {
        if (bvPreset.layout_type === layout.type) {
          return;
        }
        const stringifiedLayout = "Layout" + bvPreset.layout_type;
        allFeatures = this.getFeaturesFromLayout(stringifiedLayout, "1", allFeatures, addedNames);
      });
      // 4) check skipped layouts to get all other features another room scenes features
      const layoutNames = Object.keys(layout.layoutNames).filter(key => key !== "All");
      layoutNames.forEach((layoutType) => {
        allFeatures = this.getFeaturesFromLayout(layoutType, null, allFeatures, addedNames);
      });
      // console.log("allFeatures", allFeatures);
      return allFeatures
    }
    return this.layerPositions ? [...this.features, ...this.featuresParts] : this.features
  }

  /**
   * Builds the "unified" navigation master list from scratch.
   *
   * Every feature of the group, across ALL layouts (featuresList +
   * featuresPartsList for every layout name), is grouped by
   * getSplittedFeatureName() into a single UNavItem holding all matching
   * features in `uFeatures`. Features that are shared across layouts (e.g.
   * not-in-visualizer features are pushed to every layout) are de-duplicated by
   * reference.
   *
   * Spanning every layout is what lets getUNavigationItems() still surface a
   * name that is missing in the current RoomLayout and flag it via
   * `availableInAnotherLayout`.
   *
   * The resulting list is ~90% ready: getUNavigationItems() only has to pick the
   * right `part` per scenario instead of rebuilding everything each time.
   *
   * This is a full rebuild - it's only needed the first time the list is
   * requested (see getUNavigationItems()). After that, new features are merged
   * in one at a time via addFeatureToUFeaturesList(), so a group with a lot of
   * features doesn't have to be walked again on every single feature load.
   *
   * @returns {[UNavItem]}
   */
  createOverhaulFeaturesList() {
    this._uFeaturesMap = new Map(); // uNameId -> UNavItem, reused by addFeatureToUFeaturesList()
    this._uNavListed = new Set(); // features already in this group's master list
    this._uNavSourceSize = -1; // no incremental counting during the full build
    this.uFeaturesList = [];

    const layoutNames = this.featuresList ? Object.keys(this.featuresList) : [];
    for (let i = 0; i < layoutNames.length; i++) {
      const layoutName = layoutNames[i];
      const list = this.featuresList[layoutName] || [];
      for (let j = 0; j < list.length; j++) {
        this.addFeatureToUFeaturesList(list[j]);
      }
      // featuresParts are navigation items only for side groups (layerPositions), same as the legacy
      // getFeaturesForNavigationFrom(); for the other groups they are additional parts (e.g. crown molding part 2)
      const partsList = (this.layerPositions && this.featuresPartsList && this.featuresPartsList[layoutName]) || [];
      for (let j = 0; j < partsList.length; j++) {
        this.addFeatureToUFeaturesList(partsList[j]);
      }
    }

    this._uNavSourceSize = this.getUNavSourceSize();
    if (Config.isDevMode()) {
      console.log("this.uFeaturesList", this.uFeaturesList);
    }
    return this.uFeaturesList
  }

  /**
   * Merges a single feature into the existing `uFeaturesList` in place, instead
   * of rebuilding the whole master list. Called from `loadFeatureCommon()`
   * every time a feature finishes loading, so the unified list is kept current
   * incrementally while features stream in page by page.
   *
   * Uses the same grouping/de-dupe rule as createOverhaulFeaturesList(): a
   * feature is grouped into the UNavItem sharing its getSplittedFeatureName(),
   * and the same feature reference is never added twice (a feature can be
   * pushed into several layout lists at once, e.g. not-in-visualizer features).
   * The de-dupe check is a Set PER GROUP (`this._uNavListed`). It used to be a flag stamped on the feature, but
   * a feature instance can sit in the lists of more than one group (e.g. Corner Shelf / Small Corner Shelf,
   * shower body) and the first group that listed it hid it from the others.
   *
   * No-op if the master list hasn't been built yet - it'll be built (and will
   * already include this feature, since it's already in featuresList /
   * featuresPartsList by the time this runs) lazily on the next
   * getUNavigationItems() call, via createOverhaulFeaturesList().
   *
   * @param feature {featureInterface}
   */
  addFeatureToUFeaturesList(feature) {
    if (!this.uFeaturesList) {
      return
    }
    if (!feature || !feature.getSplittedFeatureName || this._uNavListed.has(feature)) {
      return
    }
    if (!this.layerPositions && this.featuresPartsList && typeof this.featuresPartsList.has === "function"
      && this.featuresPartsList.has(feature) && !(this.featuresList && this.featuresList.has && this.featuresList.has(feature))) {
      return // additional part, not a navigation item (same rule as createOverhaulFeaturesList)
    }
    this._uNavListed.add(feature);
    if (this._uNavSourceSize >= 0) {
      this._uNavSourceSize++;
    }
    const uNameId = this.getUNavNameId(feature);
    const existing = this._uFeaturesMap.get(uNameId);
    if (existing) {
      existing.uFeatures.push(feature);
    } else {
      const newItem = new UNavItem(uNameId, feature, [feature]);
      this._uFeaturesMap.set(uNameId, newItem);
      this.uFeaturesList.push(newItem);
    }
  }

  /**
   * Returns the navigation items for the current scenario resolved from the
   * cached UNavItem master list. Instead of recreating a navItems array on
   * every call, we clone the cached items and just resolve the correct `part`.
   *
   * For every master UNavItem we:
   *  1) narrow uFeatures by searchText (drop the item if none match)
   *  3) narrow uFeatures by the passed filters (drop the item if none match)
   *  -) resolve the scenario `item.cantRenderInVisualizer` (feature.notAvailableInVisualizer stays STATIC)
   *  2) when visualizer only, drop the item if nothing renders in this scenario
   *  4) pick the uFeature matching ALL current conditions as `part`
   *  5) if none matches: a feature in the SAME room layout that only fails the
   *     other conditions is exposed via `notMatchingMeta`; a feature that renders
   *     in a DIFFERENT room layout is exposed via `availableInAnotherLayout`
   *
   * @param filters {object|array}
   * @param searchText {string}
   * @param forFeature {object|null}
   * @param withQuotingToolFeatures {boolean|string} false = visualizer only
   * @param forActiveQTCategory {string}
   * @returns {[UNavItem]}
   */
  getUNavigationItems(filters = [], searchText = "", forFeature = null, withQuotingToolFeatures = false, forActiveQTCategory = "") {
    // rebuild when features reached the lists without passing through this group's loadFeatureCommon()
    // (lists shared between groups, `featuresList[layout] = [...]` assignments)
    if (!this.uFeaturesList || (this._uNavSourceSize >= 0 && this._uNavSourceSize !== this.getUNavSourceSize())) {
      this.createOverhaulFeaturesList();
    }
    const masterList = this.uFeaturesList || [];
    const isVanitySink = this.partId === "vanity_sink";
    const forNavigationConditions = this.getArrayOfConditions ? this.getArrayOfConditions(true) : [];
    const conditions = isVanitySink && this.getArrayOfConditions ? this.getArrayOfConditions() : []; // no need for other groups for now
    // NOTE: was taken from `conditions` only (empty for every group but vanity_sink), so availableInAnotherLayout
    // was never set for the other groups
    const roomLayoutCondition = (isVanitySink ? conditions : forNavigationConditions).find(condition => condition.propName === "RoomLayout");
    const hasFilters = !isEmpty(filters);
    const visualizerOnly = withQuotingToolFeatures === false;
    const preferredPosition = this.getUNavPreferredPosition();
    // legacy navigation only offered features of the CURRENT room layout list (some groups have no RoomLayout
    // condition at all), so the layout membership is a condition too - O(1) with LayoutFeaturesList
    const isInCurrentLayout = this.getUNavCurrentLayoutCheck();

    const navItems = [];
    for (let i = 0; i < masterList.length; i++) {
      const master = masterList[i];
      let uFeatures = master.uFeatures;

      // 1) search text: keep only the uFeatures that match the search
      if (searchText) {
        uFeatures = this.getFeaturesBySearchText(uFeatures, searchText);
        if (!uFeatures.length) {
          continue
        }
      }

      // 3) filters: keep only the uFeatures that match the active filters
      if (hasFilters) {
        uFeatures = this.getFilteredFeaturesByFilters(filters, uFeatures);
        if (!uFeatures.length) {
          continue
        }
      }

      // 3.5) toolSettings/displayLogic visibility: keep only the uFeatures that should be
      // shown for this scenario (visualizer vs quotingTool vs all), same check designNavigationItems
      // runs via isVisibleForNavigation for the non-overhaul nav items.
      uFeatures = uFeatures.filter(feature => {
        return isVisibleForNavigation(feature, withQuotingToolFeatures, forActiveQTCategory, true)
      });
      if (!uFeatures.length) {
        continue
      }

      // 4) resolve the concrete feature matching ALL current conditions. Prefer one that can render
      // (not statically flagged), then any matching one (QT / no layers).
      // NOTE: feature.notAvailableInVisualizer is STATIC (no layers / unlocked by subscription) and is
      // NOT rewritten here - lots of other code relies on it. The scenario value lives on the item
      // (`item.cantRenderInVisualizer`, read via isNavItemNotInVisualizer) and, for a single clicked/selected
      // feature, comes from isFeatureNotInVisualizerNow().
      let matchingFeature = null;
      let renderableFeature = null;
      for (let k = 0; k < uFeatures.length; k++) {
        const feature = uFeatures[k];
        const isMatchingConditions = isVanitySink
          ? feature.metaData?.["CustomVanityType"]
          && this.activeFeature?.metaData?.["CustomVanityType"]
          && (feature.metaData?.["CustomVanityType"][0] !== this.activeFeature?.metaData?.["CustomVanityType"][0])
            ? feature.matchesMetaForArrayOfConditions(forNavigationConditions)
            // when current selected is Vanity, but this is ST sink - we don't need to check all metas
            : feature.matchesMetaForArrayOfConditions(conditions) // here we are matching L & R sides
          : feature.matchesMetaForArrayOfConditions(forNavigationConditions);
        if (!isMatchingConditions || !isInCurrentLayout(feature)) {
          continue
        }
        if (!matchingFeature) {
          matchingFeature = feature;
        }
        if (!feature.notAvailableInVisualizer) { // static: has layers (or unlocked, e.g. room subscription)
          if (!preferredPosition) {
            renderableFeature = feature;
            break
          }
          // side groups (legacy navigationSubItemsWithSides): prefer the feature on the active feature's side
          if (!renderableFeature) {
            renderableFeature = feature;
          }
          if (feature.metaData && feature.metaData.layerPosition === preferredPosition) {
            renderableFeature = feature;
            break
          }
        }
      }
      const cantRenderInVisualizer = !renderableFeature;

      // 2) visualizer only: skip items that can't render in the current scenario
      if (visualizerOnly && cantRenderInVisualizer) {
        continue
      }

      const item = master.clone(false); // uFeatures is replaced right below, no need to copy the master's array
      item.uFeatures = uFeatures;
      item.availableInAnotherLayout = null;
      item.notMatchingMeta = null;
      item.cantRenderInVisualizer = cantRenderInVisualizer;

      if (renderableFeature || matchingFeature) {
        item.setPart(renderableFeature || matchingFeature);
      } else {
        // 5) nothing matches ALL the current conditions. Work out WHY from STATIC feature info
        //    (layers + RoomLayout metadata):
        //  a) a feature in the SAME room layout that only fails the other conditions
        //     (ShowerSize / VanitySize / MountType / RoomScene ...) -> notMatchingMeta.
        //     It stays notAvailableInVisualizer, but it is NOT availableInAnotherLayout.
        //  b) otherwise, a feature that renders in a DIFFERENT room layout -> availableInAnotherLayout.
        const sameLayoutFeature = uFeatures.find(feature => featureHasLayers(feature) && isInCurrentLayout(feature)
          && (!roomLayoutCondition || feature.matchesMetaForCondition(roomLayoutCondition.propName, roomLayoutCondition.propValue)));
        const anotherLayoutFeature = sameLayoutFeature
          ? null
          : uFeatures.find(feature => featureHasLayers(feature) && feature.metaData && feature.metaData.RoomLayout);

        // item.notMatchingMeta = sameLayoutFeature || null;
        item.availableInAnotherLayout = anotherLayoutFeature || null;

        // keep a representative feature as the active `part` (prefer the QT / no-layers one for
        // pricing, then the same-layout incompatible one, then the other-layout one)
        item.setPart(uFeatures.find(feature => !featureHasLayers(feature))
          || sameLayoutFeature
          || anotherLayoutFeature
          || uFeatures[0]); // item.cantRenderInVisualizer is already true
      }

      navItems.push(item);
    }

    // 7) when not visualizer only, show in-visualizer items first, then the rest
    // if (!visualizerOnly) {
    //   navItems.sort((a, b) => (a.cantRenderInVisualizer === b.cantRenderInVisualizer) ? 0 : (a.cantRenderInVisualizer ? 1 : -1));
    // } // we are sorting them later

    // 8) group specific post processing (legacy filterNavigationItems / sorting), see UNav strategy hooks below
    return this.postProcessUNavItems(navItems, {filters, searchText, forFeature, withQuotingToolFeatures, forActiveQTCategory})
  }

  // ---------------------------------------------------------------------------------------------------------------
  // UNav strategy hooks. Defaults reproduce the base IntermediatePart.navigationSubItems() behaviour, groups/visualizers
  // override only what differs. See docs/featureStructure.md (section 5).
  // ---------------------------------------------------------------------------------------------------------------

  /**
   * Grouping key of the UNav master list. Default = the legacy uniqueness rule of returnUniqueNavFeatures()
   * (toolVersion >= 5: lower cased shared feature name, bvScene sinks/faucets: splitted name, else the feature id),
   * so UNav lists the same items as the legacy navigation. bvLook overrides it with getSplittedFeatureName().
   * MUST be stable for a feature (the master list is cached).
   * @param feature {featureInterface}
   * @returns {string}
   */
  getUNavNameId(feature) {
    if (document.kvConfig.visualizerType === "bvScene" && (this.partId === "sink" || this.partId === "center_island_sink"
      || this.partId === "vanity_sink" || this.partId === "vanity_faucet")) {
      return feature.getSplittedFeatureName()
    }
    if (document.kvConfig.toolVersion >= 5) {
      return feature.getLowerCasedSharedFeatureName()
    }
    return String(feature.dbFeature.id)
  }

  /**
   * Number of features the UNav master list is built from (-1 = unknown, legacy plain featuresList objects)
   * @returns {number}
   */
  getUNavSourceSize() {
    if (!this.featuresList || typeof this.featuresList.size !== "number") {
      return -1
    }
    const partsSize = this.layerPositions && this.featuresPartsList && typeof this.featuresPartsList.size === "number"
      ? this.featuresPartsList.size
      : 0;
    return this.featuresList.size + partsSize
  }

  /**
   * @returns {function(featureInterface): boolean} is the feature in the current room layout list
   */
  getUNavCurrentLayoutCheck() {
    const parent = typeof this.getParent === "function" ? this.getParent() : null;
    const layout = this.layout || (parent && parent.layout);
    const lists = [this.featuresList, this.featuresPartsList].filter(list => list && typeof list.isInLayout === "function");
    if (!layout || !lists.length || typeof layout.getCurrentLayoutName !== "function") {
      return () => true
    }
    const layoutName = layout.getCurrentLayoutName();
    return (feature) => lists.some(list => list.isInLayout(feature, layoutName))
  }

  /**
   * When a name exists on several sides, show the one on the active feature's side (the active feature's
   * `layerPosition` metaData). Groups whose active feature has no layerPosition don't prefer any side.
   * @returns {string|null}
   */
  getUNavPreferredPosition() {
    const metaData = this.activeFeature && this.activeFeature.dbFeature && this.activeFeature.dbFeature.metaData;
    return (metaData && metaData["layerPosition"]) || null
  }

  /**
   * @param navItems {[UNavItem]}
   * @param options {{filters, searchText, forFeature, withQuotingToolFeatures, forActiveQTCategory}}
   * @returns {[UNavItem]}
   */
  postProcessUNavItems(navItems, options = {}) {
    let items = navItems;
    if (typeof this.sortUNavItems === "function") {
      items = this.sortUNavItems(items, options);
    }
    if (typeof this.filterNavigationItems === "function" && options.forFeature) {
      items = this.filterNavigationItems(items, options.forFeature); // works on navItem.part, same as for legacy items
    }
    return items
  }

  checkAutoAddToCart(feature) {
    //intermediatePart
    if (!feature.dbFeature.autoAddToCart) {
      return;
    }
    const parentGroup = this.getParent ? this.getParent() : null;// parent, for ex. "Appliance"
    if (parentGroup && parentGroup.layout && parentGroup.layout.isFakeLayout) {
      return;
    }
    const cartItem = {
      selectionGroup: (parentGroup && parentGroup.menuType) ? parentGroup.partName : this.partName,
      quantity: 1,
      partId: this.partId,
      cartFeature: feature
    };
    if (feature.dbFeature.displayLogic) {
      const storeState = store.getState();
      const {quantity, isMatching} = featureIsMatchingDisplayLogic(
        feature,
        storeState.cartState.cartItems,
        storeState.quotesState.activeQuoteIndex,
        storeState.wizardState.allWizardsValues,
        storeState.cartState.displayLogicCartItems,
        storeState.fabricatorState,
      );
      if (isMatching && (!storeState.cartState.cartItems.some(cItem => {
        return cItem.cartFeature && cItem.cartFeature.dbFeature && (cItem.cartFeature.dbFeature.id === feature.dbFeature.id)
      }))) {
        cartItem.quantity = quantity;
        store.dispatch(addToCart([cartItem]));
      }
      store.dispatch(addDisplayLogicCartItem(cartItem, this.getParent().layout.visualizerType));
    } else {
      store.dispatch(addToCart([cartItem]));
    }
  }

  loadFeatureCommon(feature) { // other logic
    this.addFeatureToUFeaturesList(feature); // keep the cached UNavItem master list current incrementally, instead of rebuilding it from scratch
    this.checkAutoAddToCart(feature);
    if (feature.dbFeature.groupName === "Room") {
      feature = setRoomNotInVisualizerProperty(feature);
    }
    if (IS_LIXIL_UI && !feature.dbFeature.clickable) {
      const layout = this.getParent().layout;
      if (layout && layout.disabledClickablePartIds && !layout.disabledClickablePartIds.includes(this.partId)) {
        layout.disabledClickablePartIds.push(this.partId);
      }
    }
    // const layout = this.getParent().layout;
    // if (feature.dbFeature.isRelatedDbFeature) {
    //   if (!Array.isArray(layout.allRelatedDbFeatures)) {
    //     layout.allRelatedDbFeatures = [];
    //   }
    //   if (!layout.allRelatedDbFeatures.some(addedDbFeature => addedDbFeature.id === feature.dbFeature.id)) {
    //     layout.allRelatedDbFeatures.push(feature.dbFeature);
    //   }
    // }
    // loadRelatedFeatures(feature.dbFeature, layout);
  }

  _setLayers(layout) {
    //intermediatePart
    if (!this.disabled) {
      if (!this.activeFeature) {
        this.setDefault(layout);
      }
      if (this.layerPositions) {
        Object.keys(this.layerPositions).forEach(key => {
          if (this.layerPositions[key]) {
            if (this.layerPositions[key].matchesMetaForArrayOfConditions(this.getArrayOfConditions())) {
              this.setFeatureLayer(this.layerPositions[key], layout);
            } else {
              this.layerPositions[key].layer.src = '';
            }
          }
        })
      } else if (this.activeFeature && !this.activeFeature.notAvailableInVisualizer) {
        if (this.partId !== "attachment") { // remove activeFeature layer to prevent duplicate, it has parts
          this.setFeatureLayer(this.activeFeature, layout);
        }
        if (this.hasAdditionalParts && (document.kvConfig.visualizerType === "kvScene")) {
          this.activeFeatureParts = this.setActiveFeatureParts(); // we have to set them each time because we need to react on disabled clicks (bottom cabinet - appliances)
        }
        if (Array.isArray(this.activeFeatureParts) && this.activeFeatureParts.length) {
          this.activeFeatureParts.forEach((activeFeaturePart) => {
            this.setFeatureLayer(activeFeaturePart, layout);
          })
        }
      }
      if (this.placeholderFeature) {
        this.setFeatureLayer(this.placeholderFeature, layout);
      }
    } else {
      this.hideLayers();
    }
  }

  setFeatureLayer(currentFeature, layout) {
    //intermediatePart
    // console.log('currentFeature', currentFeature);
    const layoutAbr = layout.getCurrentLayoutAbbreviation();
    if (!currentFeature || !currentFeature.dbFeature || !currentFeature.dbFeature.s3Bindings
      || (!(currentFeature.dbFeature.metaData["RoomLayout"]) || !currentFeature.dbFeature.metaData["RoomLayout"].includes(layoutAbr))) {
      if (currentFeature) {
        currentFeature.layer.src = "";
      }
      return;
    }
    const s3Binding = currentFeature.dbFeature.s3Bindings.find(binding => binding.visualizer === layout.layersBinding);
    currentFeature.layer.src = s3Binding ? s3Binding.path : null;
    currentFeature.layer.bucketName = s3Binding ? s3Binding.bucketName : null;
  }

  /**
   * Feature (IntermediatePart) click - shared by every visualizer (was duplicated, identical, in each
   * visualizer's intermediatePart.js). Subclasses can still override it.
   * A feature that can't render in the current scenario (static flag, or with UNav not matching
   * the current conditions) goes to `activeNotInVisualizerFeature`, keeping the current activeFeature
   * (and its layer); see promoteActiveNotInVisualizerFeature() for the layout-switch promotion.
   */
  featureClick() {
    const parent = this.getParent();
    parent.placeholderChanged = true;
    if (isFeatureNotInVisualizerNow(this)) { // static flag OR (UNav, isNewNavEnabled) not matching the current conditions
      parent.activeNotInVisualizerFeature = this; // do not change the activeFeature (in order to keep the layer)
    } else if (parent.activeNotInVisualizerFeature) {
      parent.activeNotInVisualizerFeature = null; // remove it from other searches and so on
      parent.activeFeature = this;
    } else {
      parent.activeFeature = this;
    }
    this.featureClicked();
  }

  featureClicked() {
    const featuresHistory = this.activeFeature ? this.selectedFeaturesHistory : this.getParent().selectedFeaturesHistory;
    const partName = this.activeFeature
      ? this.activeFeature.getMatchingFeatureName()
      : this.getMatchingFeatureName ? this.getMatchingFeatureName() : this.partName;
    if (Array.isArray(featuresHistory) && (featuresHistory[0] !== partName)) {
      featuresHistory.unshift(partName); // add in the beggining
      if (featuresHistory.length > 10) {
        featuresHistory.pop() // remove last element, no need to keep huge arrays
      }
    }
    // the localStorage is updated on the canvas update (see startJobLocalStorageSync)
    // other specific events, for ex. Google Analytics, etc.
  }


  // navigationSubItems(filters, searchText, forFeature, filterGroups, restOptions) { // to do
  navigationItemsWithOptions(filters, forFeature, filterGroups, featuresToDisplay) {
    // intermediate part
    let newFeatures = [];
    let navItems = [];
    const matchingFeatureProperty = this.useMatchingFeatureReplacement || "MatchingFeature";

    if (filterGroups && filterGroups.length && (!filters || !Object.keys(filters).length)
      && !IS_QKB_UI && !(CABINET_GROUP.includes(this.partId) || COUNTERTOP_GROUP.includes(this.partId))) {

      // console.log("filteredAvailableFeatures", filteredAvailableFeatures);
      // console.log("featuresToDisplay", featuresToDisplay);
      for (let i = 0; i < featuresToDisplay.length; i++) { // 1) take all others
        const currentFeature = featuresToDisplay[i];
        const colorName = currentFeature.getColorName(filterGroups);
        const matchingOptionsName = currentFeature.getMatchingOptionsName();
        let conditionPassed = !colorName
          || !newFeatures.some(feature => feature.getMatchingOptionsName() === matchingOptionsName)
          || (currentFeature.metaData[matchingFeatureProperty] && !newFeatures.some(feature => feature.metaData[matchingFeatureProperty]
                && isEqual(feature.metaData[matchingFeatureProperty], currentFeature.metaData[matchingFeatureProperty])))
        if (conditionPassed) {
          newFeatures.push(currentFeature);
        }
      }
      navItems = this.returnUniqueNavFeatures(this.getNavItemsWithOptions(forFeature, newFeatures, featuresToDisplay, filterGroups).map(featureObject => {
        return new DesignNavigationItem(featureObject.mainFeature.featureName, featureObject.mainFeature, featureObject.otherOptions)
      }), false);
    }
    if (!navItems.length) {
      navItems = this.returnUniqueNavFeatures(featuresToDisplay)
    }
    // if (this.filterNavigationItems && forFeature) {
    //   navItems = this.filterNavigationItems(navItems, forFeature)
    // }
    return navItems
  }

  /**
   * this is almost the same as feature options, it is replacing next nav items with the needed color feature
   * @param forFeature {featureInterface} featureInterface
   * @param newFeatures {[featureInterface]}
   * @param allFeatures {[featureInterface]}
   * @param filterGroups {[filterGroupInterface]}
   * @param useSpecificVanityLogic {boolean}
   * @returns {array}
   */
  getNavItemsWithOptions(forFeature= null,
                            newFeatures = [],
                            allFeatures = [],
                            filterGroups,
                            // eslint-disable-next-line no-unused-vars
                            useSpecificVanityLogic = false) {

    const matchingFeatureProperty = this.useMatchingFeatureReplacement || "MatchingFeature";
    const neededColorName = forFeature ? forFeature.getColorName(filterGroups) : "";

    return newFeatures.map(currentFeature => {
      const filterValue = getColorFilterValue(currentFeature, filterGroups);
      let otherOptions = getOptionsFromNavItemsForFeature(currentFeature, allFeatures, filterGroups);

      const currentFeatureColor = currentFeature.getColorName(filterGroups);
      const customFullDisplayName = currentFeature.getMatchingOptionsName();

      if (forFeature && (currentFeature.dbFeature.id === forFeature.dbFeature.id || (
        (customFullDisplayName === forFeature.getMatchingOptionsName())
        && (!forFeature.metaData[matchingFeatureProperty] || isEqual(forFeature.metaData[matchingFeatureProperty], currentFeature.metaData[matchingFeatureProperty]))
        && (currentFeatureColor && forFeature.getColorName(filterGroups) && (currentFeatureColor !== forFeature.getColorName(filterGroups)))
      ))) {
        if (filterValue && !otherOptions.some(option => option.colorValue.id === filterValue.id)) {
          otherOptions.push({feature: currentFeature, colorValue: filterValue})
        }
        return {
          mainFeature: forFeature,
          otherOptions: otherOptions.filter(option => option.feature.dbFeature.id !== forFeature.dbFeature.id)
        };
      } else {
        if (!filterValue) {
          return {mainFeature: currentFeature, otherOptions: []}
        }
        const allOptions = [{feature: currentFeature, colorValue: filterValue}, ...otherOptions];
        const mainOption = (forFeature && neededColorName && allOptions.find(option => option.feature.getColorName(filterGroups) === neededColorName))
        // console.log("mainOption", mainOption);
        if (mainOption) {
          return {
            mainFeature: mainOption.feature,
            otherOptions: allOptions.filter(option => option.feature.dbFeature.id !== mainOption.feature.dbFeature.id)
          }
        }
        return {mainFeature: currentFeature, otherOptions}
      }
    });
  }

  returnUniqueNavFeatures(featuresToDisplay, createNavItems = true) {
    const navItems = createNavItems ? this.returnNavItemsFromFeatures(featuresToDisplay) : featuresToDisplay;
    if (document.kvConfig.visualizerType === "bvScene" && (this.partId ==="sink" || this.partId ==="center_island_sink"
      || this.partId ==="vanity_sink" || this.partId ==="vanity_faucet")) {
      return uniqBy(navItems, (navItem) => navItem.part.dbFeature.id && navItem.part.getSplittedFeatureName());
    }
    if (document.kvConfig.toolVersion >= 5) { // not sure if we can do that for old visualizers
      return uniqBy(navItems, (navItem) => navItem.part.dbFeature.id && navItem.part.getLowerCasedSharedFeatureName());
    }
    return uniqBy(navItems, (navItem) => navItem.part.dbFeature.id);
  }

  returnNavItemsFromFeatures(featuresToDisplay) {
    return featuresToDisplay.map(_ => new DesignNavigationItem(_.featureName, _));
  }

  clone() {
    // "fridge", "fridge_enclosure", "fridge_enclosure_handle", "bathtub_body", "grab_bar"
    return Object.assign(Object.create(Object.getPrototypeOf(this)), this);
  }

}

PartCommon.prototype.getFeatureCurrency = function (propsFilterGroups) {
  if (Object.hasOwnProperty(this._useCurrency)) {
    return this._useCurrency
  }
  this._useCurrency = null;
  const filterGroups = propsFilterGroups || store.getState().filterState.filterGroups;
  const filterGroup = filterGroups.find(group => group.name === "Currency");
  if (!filterGroup) {
    return this._useCurrency
  }
  const featureCollectionValue = getFilterGroupValues(this, filterGroup);
  if (Array.isArray(featureCollectionValue)) {
    const featureCollectionValueObject = getFilterGroupValue(featureCollectionValue[0], filterGroup);
    if (featureCollectionValueObject) {
      this._useCurrency = featureCollectionValueObject.value
    }
  }
  return this._useCurrency
}

PartCommon.prototype.getFeatureRelatedFeatures = function () {
  if (!this._relatedFeaturesSet) {
    // console.log("loadRelatedFeatures", this.dbFeature);
    loadRelatedFeatures(this.dbFeature, this.getParent().getParent().layout);
    this._relatedFeaturesSet = true;
  }
  return this.dbFeature.relatedFeatures
}
